import { MsalAuthenticationTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { apiConfig, loginRequest } from "../../authConfig";

import { Loading } from "../../components/Loading";
import { ErrorComponent } from "../../components/ErrorComponent";
import React, { useContext, useEffect, useState } from "react";
import { GlobalContext } from "../../utils/GlobalContext";
import ProtectedPage from "../../utils/ProtectedPage";

type Source = 0 | 1 | 2; // 0 Missing, 1 Mapped, 2 Fuzzy

interface ZohoSlot {
    source: Source;
    id?: string | null;
    name?: string | null;
    childFirstName?: string | null;
    childLastName?: string | null;
    dateOfBirth?: string | null;
    abaTentativeAssessmentDateTime?: string | null;
    lacCenter?: string | null;
    lacCenterState?: string | null;
    hasTentativeAssmtScheduledPhase?: boolean;
    hasValidCenter?: boolean;
    fuzzyScore?: number | null;
}

interface CoreSlot {
    source: Source;
    id?: number | null;
    firstName?: string | null;
    lastName?: string | null;
    dob?: string | null;
    zohoId?: string | null;
    centralReachContactId?: number | null;
    active?: boolean | null;
    centerName?: string | null;
    stateName?: string | null;
    hasValidCenter?: boolean;
    zohoMappedAt?: string | null;
    zohoMappedBy?: string | null;
    crMappedAt?: string | null;
    crMappedBy?: string | null;
}

interface CRSlot {
    source: Source;
    contactId?: number | null;
    firstName?: string | null;
    lastName?: string | null;
    birthDate?: string | null;
    isActive?: boolean | null;
    fuzzyScore?: number | null;
}

interface AuditStatus {
    overall: "Mapped" | "PartiallyMapped" | "NotMapped" | string;
    zohoIdLinked: boolean;
    crIdLinked: boolean;
    firstNameMatch: boolean;
    lastNameMatch: boolean;
    dobMatch: boolean;
    tentativeAssigned: boolean;
    reasons: string[];
}

interface MapAuditResult {
    zoho?: ZohoSlot | null;
    core?: CoreSlot | null;
    cr?: CRSlot | null;
    status: AuditStatus;
    found: boolean;
    message?: string | null;
}

const fmtDate = (d?: string | null) => {
    if (!d) return "—";
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    return date.toLocaleDateString();
};

const fmtDateTime = (d?: string | null) => {
    if (!d) return "—";
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    return date.toLocaleString();
};

// Convert the backend's raw fuzzy score (rough range 0–200+) to a 0–100% confidence.
// 200 ≈ "all expected signals matched" → 100%. Clamped on both ends.
const toConfidencePct = (score?: number | null): number | null => {
    if (score == null) return null;
    return Math.round(Math.max(0, Math.min(100, (score / 200) * 100)));
};

const stateAbbrev = (full?: string | null): string => {
    if (!full) return "";
    const trimmed = full.trim();
    if (trimmed.length === 2) return trimmed.toUpperCase(); // already an abbreviation
    const m: Record<string, string> = {
        alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
        colorado: "CO", connecticut: "CT", delaware: "DE", "district of columbia": "DC",
        florida: "FL", georgia: "GA", hawaii: "HI", idaho: "ID", illinois: "IL",
        indiana: "IN", iowa: "IA", kansas: "KS", kentucky: "KY", louisiana: "LA",
        maine: "ME", maryland: "MD", massachusetts: "MA", michigan: "MI", minnesota: "MN",
        mississippi: "MS", missouri: "MO", montana: "MT", nebraska: "NE", nevada: "NV",
        "new hampshire": "NH", "new jersey": "NJ", "new mexico": "NM", "new york": "NY",
        "north carolina": "NC", "north dakota": "ND", ohio: "OH", oklahoma: "OK",
        oregon: "OR", pennsylvania: "PA", "rhode island": "RI", "south carolina": "SC",
        "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT", vermont: "VT",
        virginia: "VA", washington: "WA", "west virginia": "WV", wisconsin: "WI", wyoming: "WY"
    };
    return m[trimmed.toLowerCase()] ?? trimmed;
};

const sourceBadge = (s?: Source) => {
    if (s === 1 || s === 2) return <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 font-semibold">Exists</span>;
    return <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800 font-semibold">Missing</span>;
};

const Check = ({ ok, label }: { ok: boolean; label: string }) => (
    <div className="flex items-center text-sm">
        <span className={"mr-2 inline-flex items-center justify-center w-5 h-5 rounded-full " + (ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700")}>
            {ok ? "✓" : "✗"}
        </span>
        <span className={ok ? "text-gray-700" : "text-red-700"}>{label}</span>
    </div>
);

type FieldState = "green" | "yellow" | "red";

const FieldMarker = ({ state, label, tooltip }: { state: FieldState; label: string; tooltip?: string }) => {
    const palette =
        state === "green" ? { bg: "bg-green-100", fg: "text-green-700", glyph: "✓", text: "text-gray-700" } :
        state === "yellow" ? { bg: "bg-amber-100", fg: "text-amber-700", glyph: "!", text: "text-amber-800" } :
                             { bg: "bg-red-100",   fg: "text-red-700",   glyph: "✗", text: "text-red-700" };

    return (
        <div className="flex items-start text-sm py-0.5">
            <span className={`mr-2 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full shrink-0 font-bold leading-none ${palette.bg} ${palette.fg}`}>
                <span className="leading-none">{palette.glyph}</span>
            </span>
            <div className="flex flex-col">
                <span className={palette.text}>{label}</span>
                {state === "yellow" && tooltip && (
                    <span className="text-xs text-amber-800 italic mt-0.5">{tooltip}</span>
                )}
            </div>
        </div>
    );
};

// A field is GREEN when it has a value AND (no cross-system disagreement was flagged).
// YELLOW: the side IS mapped (source=1) but the field is missing or disagrees — kept due to historical link.
// RED: not mapped (fuzzy or missing) and the field is missing/unusable.
function fieldStatus(opts: {
    hasValue: boolean;
    agrees: boolean;        // matches the other populated sides on this same field
    sourceIsMapped: boolean; // true when this column's record came from the TblClient link, not a fuzzy fallback
}): { state: FieldState; tooltip?: string } {
    const { hasValue, agrees, sourceIsMapped } = opts;
    if (hasValue && agrees) return { state: "green" };
    if (sourceIsMapped) {
        const reason = !hasValue
            ? "This field is missing/null in the linked record, but the record was mapped from historical data anyways."
            : "This field does not match the value on the other systems, but the record was mapped from historical data anyways.";
        return { state: "yellow", tooltip: reason };
    }
    return { state: "red" };
}

const ZohoCrMapAuditContent = () => {
    const globalContext = useContext(GlobalContext);

    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [clientId, setClientId] = useState<string>("");
    const [zohoIdIn, setZohoIdIn] = useState<string>("");
    const [crIdIn, setCrIdIn] = useState<string>("");
    const [showIdSearch, setShowIdSearch] = useState<boolean>(false);

    const [loading, setLoading] = useState<boolean>(false);
    const [result, setResult] = useState<MapAuditResult | null>(null);

    const [drawer, setDrawer] = useState<null | { type: "zoho" | "core" | "cr"; data: any; loading: boolean }>(null);

    const [tab, setTab] = useState<"search" | "recent" | "dupes">("search");
    const [pendingSearchTrigger, setPendingSearchTrigger] = useState<number>(0);

    function buildHeaders(token: string) {
        const h = new Headers();
        h.append("Authorization", "Bearer " + token);
        return h;
    }

    // Name-search and ID-search are mutually exclusive: typing in one group clears the other.
    // The backend prioritizes any ID over the name query, so a stale ClientId (e.g. left over
    // from a Recent-tab "View") would otherwise keep returning the previously-navigated record.
    function clearIdInputs() { setClientId(""); setZohoIdIn(""); setCrIdIn(""); }
    function clearNameInputs() { setFirstName(""); setLastName(""); }

    const nameModeFilled = firstName.trim() !== "" && lastName.trim() !== "";
    const namePartial = (firstName.trim() !== "") !== (lastName.trim() !== "");
    const idFilled = clientId !== "" || zohoIdIn !== "" || crIdIn !== "";
    const canSearch = nameModeFilled || idFilled;

    // Open a record in the Search tab by Core client id or Zoho learner id. Clears the other
    // inputs, switches to the Search tab, and bumps a counter that an effect watches to run
    // the search once the input state has actually been applied. Fired by the Recent tab's
    // "View" button and the Duplicates tab's client / learner links.
    function openByIds(ids: { clientId?: number | string; zohoId?: string }) {
        setFirstName("");
        setLastName("");
        setClientId(ids.clientId != null ? String(ids.clientId) : "");
        setZohoIdIn(ids.zohoId != null ? ids.zohoId : "");
        setCrIdIn("");
        setShowIdSearch(true);
        setTab("search");
        setPendingSearchTrigger(t => t + 1);
    }
    function openInSearch(coreClientId: number) { openByIds({ clientId: coreClientId }); }
    function openZohoInSearch(zohoId: string) { openByIds({ zohoId }); }

    useEffect(() => {
        if (pendingSearchTrigger > 0 && (clientId !== "" || zohoIdIn !== "" || crIdIn !== "")) {
            runSearch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingSearchTrigger]);

    function runSearch() {
        if (!canSearch) return;

        setLoading(true);
        setResult(null);
        globalContext.GetToken().then((token: any) => {
            const params = new URLSearchParams();
            if (nameModeFilled) params.set("q", `${firstName.trim()} ${lastName.trim()}`);
            if (clientId) params.set("clientId", clientId);
            if (zohoIdIn) params.set("zohoId", zohoIdIn);
            if (crIdIn) params.set("crContactId", crIdIn);

            fetch(`${apiConfig.apiEndpoint}/MapAudit/Search?${params.toString()}`, {
                method: "GET",
                headers: buildHeaders(token)
            })
                .then(r => r.json())
                .then(
                    (data: MapAuditResult) => {
                        setResult(data);
                        setLoading(false);
                    },
                    (err) => {
                        setLoading(false);
                        globalContext.setErrorModalProps({ errorMessage: String(err), errorLocation: "/MapAudit/Search" });
                        globalContext.setErrorModalActive(true);
                    }
                );
        });
    }

    function openDrawer(type: "zoho" | "core" | "cr") {
        let url = "";
        if (type === "zoho" && result?.zoho?.id) url = `/MapAudit/GetZohoDetail?zohoId=${result.zoho.id}`;
        else if (type === "core" && result?.core?.id) url = `/MapAudit/GetTblClientDetail?clientId=${result.core.id}`;
        else if (type === "cr" && result?.cr?.contactId) url = `/MapAudit/GetCRDetail?contactId=${result.cr.contactId}`;
        else return;

        setDrawer({ type, data: null, loading: true });

        globalContext.GetToken().then((token: any) => {
            fetch(`${apiConfig.apiEndpoint}${url}`, { method: "GET", headers: buildHeaders(token) })
                .then(r => r.json())
                .then(
                    (data) => setDrawer({ type, data, loading: false }),
                    () => setDrawer({ type, data: { error: "Failed to load" }, loading: false })
                );
        });
    }

    const overallBanner = () => {
        if (!result || !result.found) return null;
        const s = result.status;
        const overallForDisplay = (crSyncDelayed && s.overall === "PartiallyMapped") ? "Mapped" : s.overall;
        const bg =
            overallForDisplay === "Mapped" ? "bg-green-100 border-green-400 text-green-900"
                : overallForDisplay === "PartiallyMapped" ? "bg-amber-100 border-amber-400 text-amber-900"
                    : "bg-red-100 border-red-400 text-red-900";
        const label =
            overallForDisplay === "Mapped" ? "✓ FULLY MAPPED"
                : overallForDisplay === "PartiallyMapped" ? "⚠ MAPPED BUT MISMATCHED"
                    : "✗ NOT MAPPED";

        return (
            <div className={`border-2 rounded-lg p-3 mb-4 ${bg}`}>
                <div className="text-lg font-bold">{label}</div>
                {crSyncDelayed && (
                    <div className="text-sm italic mt-1">{crDelayedMessage}</div>
                )}
            </div>
        );
    };

    const rowClass = (_?: any) => "py-1";

    // Pairwise field-comparison status (used inside the bridge mapping boxes).
    function pairStatus(a: string | null | undefined, b: string | null | undefined, linkPresent: boolean) {
        const av = (a ?? "").trim().toLowerCase();
        const bv = (b ?? "").trim().toLowerCase();
        const hasBoth = av !== "" && bv !== "";
        const agrees = hasBoth && av === bv;
        if (agrees) return { state: "green" as FieldState };
        if (linkPresent) {
            const reason = !hasBoth
                ? "Field is missing on one side, but the records are linked from historical data."
                : "Values disagree between the two systems, but the records are linked from historical data.";
            return { state: "yellow" as FieldState, tooltip: reason };
        }
        return { state: "red" as FieldState };
    }

    function pairDob(a: string | null | undefined, b: string | null | undefined, linkPresent: boolean) {
        const ad = a ? new Date(a) : null;
        const bd = b ? new Date(b) : null;
        const aOk = ad && !isNaN(ad.getTime());
        const bOk = bd && !isNaN(bd.getTime());
        const hasBoth = aOk && bOk;
        const agrees = hasBoth && ad!.toDateString() === bd!.toDateString();
        if (agrees) return { state: "green" as FieldState };
        if (linkPresent) {
            const reason = !hasBoth
                ? "DOB is missing or unparseable on one side, but the records are linked from historical data."
                : "DOBs disagree between the two systems, but the records are linked from historical data.";
            return { state: "yellow" as FieldState, tooltip: reason };
        }
        return { state: "red" as FieldState };
    }

    // Existence check helper: green when value present, yellow when missing but the link exists, red otherwise.
    function existsCheck(hasValue: boolean, linkPresent: boolean, missingReason: string) {
        if (hasValue) return { state: "green" as FieldState };
        if (linkPresent) return { state: "yellow" as FieldState, tooltip: missingReason };
        return { state: "red" as FieldState };
    }

    // Box 1: Zoho ↔ Core mapping — mirrors the CreateClientsFromLearners job in AzureFunctionZoho/Zoho.cs.
    // A Zoho learner gets a Core client created when all of these are true:
    //   - first name, last name, DOB are filled in
    //   - learner has reached the "ABA Tentative Assmt Scheduled" phase
    //   - learner's center resolves to a Core center (with a state)
    const zohoCoreBox = () => {
        const z = result?.zoho;
        const c = result?.core;
        if (!z && !c) {
            return wrapBridge("red", "Zoho → Core", "No records on either side.", null);
        }
        const linkPresent = !!(c?.zohoId);

        const first = existsCheck(!!z?.childFirstName, linkPresent, "First name is blank in Zoho, but the two records are already linked from historical data.");
        const last = existsCheck(!!z?.childLastName, linkPresent, "Last name is blank in Zoho, but the two records are already linked from historical data.");
        const dob = existsCheck(!!z?.dateOfBirth, linkPresent, "Date of birth is blank in Zoho, but the two records are already linked from historical data.");
        const tentative = existsCheck(!!z?.hasTentativeAssmtScheduledPhase, linkPresent, "Zoho learner hasn't reached the 'Tentative Assessment Scheduled' phase yet, but the two records are already linked from historical data.");
        const state = existsCheck(!!z?.hasValidCenter, linkPresent, "Zoho learner's center doesn't resolve to a Core center, but the two records are already linked from historical data.");

        const allGreen = linkPresent && [first, last, dob, tentative, state].every(s => s.state === "green");
        const tone = allGreen ? "green" : linkPresent ? "yellow" : "red";

        return wrapBridge(tone, "Zoho → Core", null, (
            <>
                <FieldMarker state={first.state} tooltip={first.tooltip} label="First name is filled in on the Zoho learner" />
                <FieldMarker state={last.state} tooltip={last.tooltip} label="Last name is filled in on the Zoho learner" />
                <FieldMarker state={dob.state} tooltip={dob.tooltip} label="Date of birth is filled in on the Zoho learner" />
                <FieldMarker state={tentative.state} tooltip={tentative.tooltip} label="Zoho learner has been scheduled for tentative assessment" />
                <FieldMarker state={state.state} tooltip={state.tooltip} label="Zoho learner's center resolves to a Core center" />
                {mappedAtLine(c?.zohoMappedAt, c?.zohoMappedBy)}
            </>
        ));
    };

    // Box 2: Core ↔ CR mapping — checks that the required Core fields exist and the learner is active.
    const coreCrBox = () => {
        const c = result?.core;
        const cr = result?.cr;
        if (!c && !cr) {
            return wrapBridge("red", "Core ↔ CR", "No records on either side.", null);
        }
        const linkPresent = !!(c?.centralReachContactId);

        const first = existsCheck(!!c?.firstName, linkPresent, "First name is blank in Core, but the two records are already linked from historical data.");
        const last = existsCheck(!!c?.lastName, linkPresent, "Last name is blank in Core, but the two records are already linked from historical data.");
        const dob = existsCheck(!!c?.dob, linkPresent, "Date of birth is blank in Core, but the two records are already linked from historical data.");
        const activeActual = c?.active === null || c?.active === undefined ? "null" : String(c?.active);
        const active = existsCheck(c?.active === true, linkPresent, `Core learner Active = ${activeActual}, not true — but the two records are already linked from historical data.`);
        const state = existsCheck(!!c?.hasValidCenter, linkPresent, "Core learner doesn't have a valid center and state, but the two records are already linked from historical data.");

        const allGreen = linkPresent && [first, last, dob, active, state].every(s => s.state === "green");
        const tone = allGreen ? "green" : linkPresent ? "yellow" : "red";

        return wrapBridge(tone, "Core → CR", null, (
            <>
                <FieldMarker state={first.state} tooltip={first.tooltip} label="First name is filled in on the Core learner" />
                <FieldMarker state={last.state} tooltip={last.tooltip} label="Last name is filled in on the Core learner" />
                <FieldMarker state={dob.state} tooltip={dob.tooltip} label="Date of birth is filled in on the Core learner" />
                <FieldMarker state={active.state} tooltip={active.tooltip} label="Learner is active in Core" />
                <FieldMarker state={state.state} tooltip={state.tooltip} label="Core learner has a valid center and state" />
                {crSyncDelayed && (
                    <div className="text-xs text-amber-800 italic mt-2 bg-amber-50 border border-amber-300 rounded p-2">
                        {crDelayedMessage}
                    </div>
                )}
                {mappedAtLine(c?.crMappedAt, c?.crMappedBy)}
            </>
        ));
    };

    // Edge case: TblClient.CentralReachContactId is set, but the CR sync hasn't pulled the
    // Contact row into our local CR.Contacts table yet. If the mapping happened within the
    // last 24 hours, surface that explicitly instead of showing a "mismatched" warning.
    const crSyncDelayed: boolean = !!(
        result?.core?.centralReachContactId &&
        (!result?.cr || result.cr.source !== 1) &&
        result?.core?.crMappedAt &&
        (Date.now() - new Date(result.core.crMappedAt).getTime()) < 24 * 60 * 60 * 1000
    );
    const crDelayedMessage = "Mapped but CR data is slightly delayed. It may take up to 24 hours to get this data.";

    function mappedAtLine(when?: string | null, _by?: string | null) {
        if (!when) return null;
        const d = new Date(when);
        const formatted = isNaN(d.getTime())
            ? when
            : d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
        return (
            <div className="text-xs text-gray-600 italic mt-2">
                Mapped: {formatted}
            </div>
        );
    }

    function wrapBridge(tone: "green" | "yellow" | "red", title: string, blurb: string | null, body: React.ReactNode) {
        const palette =
            tone === "red" ? "border-red-400 bg-red-50" :
                             "border-green-400 bg-green-50";
        const headBadge =
            tone === "red"
                ? <span className="text-xs px-2 py-0.5 rounded-full bg-red-200 text-red-900 font-semibold">NOT MAPPED</span>
                : <span className="text-xs px-2 py-0.5 rounded-full bg-green-200 text-green-900 font-semibold">MAPPED</span>;
        return (
            <div className={`border-2 rounded-lg p-3 ${palette} h-full flex flex-col`}>
                <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-sm">{title}</span>
                    {headBadge}
                </div>
                <div className="text-6xl font-bold text-gray-600 text-center leading-none my-2 select-none">⟶</div>
                {blurb && <div className="text-sm">{blurb}</div>}
                {body}
            </div>
        );
    }

    const columnHeader = (title: string, src?: Source, color?: string) => (
        <div className={`p-3 border-b-2 ${color}`}>
            <div className="flex items-center justify-between">
                <span className="font-bold text-lg">{title}</span>
                {sourceBadge(src)}
            </div>
        </div>
    );

    const dobsAgree =
        result?.status?.dobMatch ?? true;
    const firstsAgree = result?.status?.firstNameMatch ?? true;
    const lastsAgree = result?.status?.lastNameMatch ?? true;

    const cellClass = (ok: boolean) => "py-1 " + (ok ? "" : "text-red-700 font-semibold");

    return (
        <div className="flex-1 p-5">
            <h2 className="text-2xl font-semibold mb-4">Zoho ↔ Core ↔ CR Map Audit</h2>

            {/* Tab strip */}
            <div className="flex border-b border-gray-300 mb-4">
                <button
                    onClick={() => setTab("search")}
                    className={`px-5 py-2 text-sm font-semibold border-b-2 transition-colors ${
                        tab === "search"
                            ? "border-blue text-blue"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                >
                    Search
                </button>
                <button
                    onClick={() => setTab("recent")}
                    className={`px-5 py-2 text-sm font-semibold border-b-2 transition-colors ${
                        tab === "recent"
                            ? "border-blue text-blue"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                >
                    Recent Mappings
                </button>
                <button
                    onClick={() => setTab("dupes")}
                    className={`px-5 py-2 text-sm font-semibold border-b-2 transition-colors ${
                        tab === "dupes"
                            ? "border-blue text-blue"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                >
                    Duplicates
                </button>
            </div>

            {tab === "recent" && (
                <RecentTab globalContext={globalContext} onView={openInSearch} />
            )}

            {tab === "dupes" && (
                <DuplicatesTab
                    globalContext={globalContext}
                    onOpenClient={openInSearch}
                    onOpenLearner={openZohoInSearch}
                />
            )}

            <div style={{ display: tab === "search" ? undefined : "none" }}>

            <div className="bg-white border border-gray-300 rounded-lg p-4 mb-4 shadow-sm">
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                    <div className="text-sm text-gray-600">
                        Search by <span className="font-semibold">First + Last name</span>, or by any one ID.
                    </div>
                    <label className="inline-flex items-center cursor-pointer select-none">
                        <span className="mr-3 text-sm font-medium text-gray-700">Show ID search options</span>
                        <span className="relative">
                            <input
                                type="checkbox"
                                className="sr-only peer"
                                checked={showIdSearch}
                                onChange={() => setShowIdSearch(v => !v)}
                            />
                            <span className="block w-11 h-6 bg-gray-300 rounded-full peer-checked:bg-blue transition-colors" />
                            <span className="absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full shadow transition-transform peer-checked:translate-x-5" />
                        </span>
                    </label>
                </div>

                {/* Name pair — joined with AND */}
                <div className={`border-2 rounded-lg p-3 ${namePartial ? "border-amber-400 bg-amber-50" : "border-gray-200 bg-gray-50"}`}>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Option A — search by name</div>
                    <div className="flex items-end gap-2">
                        <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                First name {namePartial && firstName.trim() === "" && <span className="text-amber-700">(required)</span>}
                            </label>
                            <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-3 py-2"
                                placeholder="e.g. Jane"
                                value={firstName}
                                onChange={e => { setFirstName(e.target.value); clearIdInputs(); }}
                                onKeyDown={e => { if (e.key === "Enter") runSearch(); }}
                            />
                        </div>
                        <div className="font-bold text-gray-500 pb-2 text-sm select-none">AND</div>
                        <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                Last name {namePartial && lastName.trim() === "" && <span className="text-amber-700">(required)</span>}
                            </label>
                            <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-3 py-2"
                                placeholder="e.g. Doe"
                                value={lastName}
                                onChange={e => { setLastName(e.target.value); clearIdInputs(); }}
                                onKeyDown={e => { if (e.key === "Enter") runSearch(); }}
                            />
                        </div>
                    </div>
                </div>

                {/* IDs — any one of (collapsed by default) */}
                {showIdSearch && (
                <>
                <div className="flex items-center my-4">
                    <div className="flex-1 border-t border-gray-300" />
                    <div className="px-3 text-sm font-bold text-gray-500 select-none">— OR —</div>
                    <div className="flex-1 border-t border-gray-300" />
                </div>

                <div className="border-2 border-gray-200 bg-gray-50 rounded-lg p-3">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Option B — search by ID (any one)</div>
                    <div className="flex items-end gap-2">
                        <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Zoho ID</label>
                            <input
                                type="text"
                                className="w-full border border-gray-300 rounded px-3 py-2"
                                placeholder="e.g. 6163541000050905498"
                                value={zohoIdIn}
                                onChange={e => { setZohoIdIn(e.target.value); clearNameInputs(); }}
                                onKeyDown={e => { if (e.key === "Enter") runSearch(); }}
                            />
                        </div>
                        <div className="font-bold text-gray-500 pb-2 text-sm select-none">OR</div>
                        <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Core ClientId</label>
                            <input
                                type="number"
                                className="w-full border border-gray-300 rounded px-3 py-2"
                                placeholder="e.g. 12345"
                                value={clientId}
                                onChange={e => { setClientId(e.target.value); clearNameInputs(); }}
                                onKeyDown={e => { if (e.key === "Enter") runSearch(); }}
                            />
                        </div>
                        <div className="font-bold text-gray-500 pb-2 text-sm select-none">OR</div>
                        <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-600 mb-1">CR ContactId</label>
                            <input
                                type="number"
                                className="w-full border border-gray-300 rounded px-3 py-2"
                                placeholder="e.g. 8675309"
                                value={crIdIn}
                                onChange={e => { setCrIdIn(e.target.value); clearNameInputs(); }}
                                onKeyDown={e => { if (e.key === "Enter") runSearch(); }}
                            />
                        </div>
                    </div>
                </div>
                </>
                )}

                <div className="mt-4 flex items-center gap-3">
                    <button
                        className="bg-blue text-white font-semibold px-6 py-2 rounded hover:opacity-90 disabled:opacity-50"
                        onClick={runSearch}
                        disabled={loading || !canSearch}
                        title={!canSearch ? "Provide First + Last together, or any one of the IDs" : ""}
                    >
                        {loading ? "Searching…" : "Search"}
                    </button>
                    {!canSearch && (
                        <span className="text-xs text-gray-500 italic">
                            Provide First + Last, or one ID.
                        </span>
                    )}
                </div>
            </div>

            {loading && <Loading />}

            {result && !result.found && (
                <div className="bg-gray-100 border border-gray-300 rounded p-4 text-gray-700">
                    {result.message || "No matches found."}
                </div>
            )}

            {result && result.found && (
                <>
                    {overallBanner()}

                    <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr_3fr_2fr_3fr] gap-3 items-stretch">
                        {/* ZOHO */}
                        <div
                            className={`border-2 rounded-lg bg-white shadow-sm cursor-pointer hover:shadow-md ${result.zoho?.source === 2 ? "border-red-400" : "border-gray-300"}`}
                            onClick={() => result.zoho && openDrawer("zoho")}
                        >
                            {columnHeader("Zoho", result.zoho?.source, "border-purple-300")}
                            {result.zoho ? (
                                <div className="p-3 text-sm">
                                    <div className={"py-1"}><span className="text-gray-500">First:</span> {result.zoho.childFirstName ?? <span className="text-red-700">NULL</span>}</div>
                                    <div className={"py-1"}><span className="text-gray-500">Last:</span> {result.zoho.childLastName ?? <span className="text-red-700">NULL</span>}</div>
                                    <div className={"py-1"}><span className="text-gray-500">DOB:</span> {fmtDate(result.zoho.dateOfBirth)}</div>
                                    <div className={"py-1"}><span className="text-gray-500">Tentative Sched:</span> {fmtDateTime(result.zoho.abaTentativeAssessmentDateTime)}</div>
                                    <div className={"py-1"}><span className="text-gray-500">Center:</span> {result.zoho.lacCenter ? `${result.zoho.lacCenter}${result.zoho.lacCenterState ? ` (${result.zoho.lacCenterState})` : ""}` : "—"}</div>
                                    <div className="py-1"><span className="text-gray-500">Name:</span> {result.zoho.name ?? "—"}</div>
                                    <div className="py-1"><span className="text-gray-500">Zoho Id:</span> {result.zoho.id ?? "—"}</div>
                                    {result.zoho.source === 2 && (
                                        <div className="mt-2 text-xs bg-red-50 border border-red-300 rounded p-2 text-red-800">
                                            <span className="font-semibold">Not linked to Core.</span> The system found this Zoho learner as a possible match — {toConfidencePct(result.zoho.fuzzyScore) ?? "—"}% confidence it's the right learner.
                                        </div>
                                    )}
                                    <div className="mt-2 text-xs text-blue underline">Click for full detail →</div>
                                </div>
                            ) : <div className="p-3 text-gray-500 italic">No Zoho record found.</div>}
                        </div>

                        {/* ZOHO ↔ CORE bridge */}
                        {zohoCoreBox()}

                        {/* CORE */}
                        <div
                            className={`border-2 rounded-lg bg-white shadow-sm cursor-pointer hover:shadow-md ${result.core ? "border-gray-300" : "border-red-400"}`}
                            onClick={() => result.core && openDrawer("core")}
                        >
                            {columnHeader("Core", result.core?.source, "border-blue-300")}
                            {result.core ? (
                                <div className="p-3 text-sm">
                                    <div className={"py-1"}><span className="text-gray-500">First:</span> {result.core.firstName ?? <span className="text-red-700">NULL</span>}</div>
                                    <div className={"py-1"}><span className="text-gray-500">Last:</span> {result.core.lastName ?? <span className="text-red-700">NULL</span>}</div>
                                    <div className={"py-1"}><span className="text-gray-500">DOB:</span> {fmtDate(result.core.dob)}</div>
                                    <div className={"py-1"}><span className="text-gray-500">Active:</span> {String(result.core.active ?? "")}</div>
                                    <div className="py-1"><span className="text-gray-500">Center:</span> {result.core.centerName ? `${result.core.centerName}${result.core.stateName ? ` (${stateAbbrev(result.core.stateName)})` : ""}` : "—"}</div>
                                    <div className="py-1"><span className="text-gray-500">ClientId:</span> {result.core.id ?? "—"}</div>
                                    <div className="mt-2 text-xs text-blue underline">Click for full detail & audit →</div>
                                </div>
                            ) : <div className="p-3 text-gray-500 italic">No TblClient row.</div>}
                        </div>

                        {/* CORE ↔ CR bridge */}
                        {coreCrBox()}

                        {/* CR */}
                        <div
                            className={`border-2 rounded-lg bg-white shadow-sm cursor-pointer hover:shadow-md ${result.cr?.source === 2 ? "border-red-400" : "border-gray-300"}`}
                            onClick={() => result.cr && openDrawer("cr")}
                        >
                            {columnHeader("CentralReach", result.cr?.source, "border-cyan-300")}
                            {result.cr ? (
                                <div className="p-3 text-sm">
                                    <div className={"py-1"}><span className="text-gray-500">First:</span> {result.cr.firstName ?? <span className="text-red-700">NULL</span>}</div>
                                    <div className={"py-1"}><span className="text-gray-500">Last:</span> {result.cr.lastName ?? <span className="text-red-700">NULL</span>}</div>
                                    <div className={"py-1"}><span className="text-gray-500">DOB:</span> {result.cr.birthDate ?? "—"}</div>
                                    <div className="py-1"><span className="text-gray-500">Active:</span> {String(result.cr.isActive ?? "")}</div>
                                    <div className="py-1"><span className="text-gray-500">ContactId:</span> {result.cr.contactId ?? "—"}</div>
                                    {result.cr.source === 2 && (
                                        <div className="mt-2 text-xs bg-red-50 border border-red-300 rounded p-2 text-red-800">
                                            <span className="font-semibold">Not linked to Core.</span> The system found this CentralReach contact as a possible match — {toConfidencePct(result.cr.fuzzyScore) ?? "—"}% confidence it's the right learner.
                                        </div>
                                    )}
                                    <div className="mt-2 text-xs text-blue underline">Click for full detail & audit →</div>
                                </div>
                            ) : (
                                <div className="p-3 text-sm italic">
                                    {crSyncDelayed
                                        ? <span className="text-amber-800">{crDelayedMessage}</span>
                                        : <span className="text-gray-500">No CR contact found.</span>}
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

            {drawer && (
                <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-end" onClick={() => setDrawer(null)}>
                    <div
                        className="w-full md:w-2/3 lg:w-1/2 h-full bg-white overflow-y-auto shadow-2xl p-5"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold">
                                {drawer.type === "zoho" && "Zoho Learner — Full Detail"}
                                {drawer.type === "core" && "Core TblClient — Full Detail + Audit"}
                                {drawer.type === "cr" && "CR Contact — Full Detail + Audit"}
                            </h3>
                            <button className="text-gray-500 hover:text-black text-xl" onClick={() => setDrawer(null)}>✕</button>
                        </div>

                        {drawer.loading && <Loading />}

                        {!drawer.loading && drawer.data && (
                            <DrawerContent type={drawer.type} data={drawer.data} />
                        )}
                    </div>
                </div>
            )}

            </div>{/* /tab search wrapper */}
        </div>
    );
};

interface RecentMappingRow {
    clientId: number;
    firstName?: string | null;
    lastName?: string | null;
    mappingKind: "ZohoToCore" | "CoreToCR" | string;
    mappedAt: string;
    mappedBy?: string | null;
    externalId?: string | null;
}
interface RecentMappingsResult {
    rows: RecentMappingRow[];
    hasMore: boolean;
}

const PAGE_SIZE = 25;

const RecentTab = ({ globalContext, onView }: { globalContext: any; onView: (clientId: number) => void }) => {
    const [mode, setMode] = useState<"both" | "zoho" | "cr">("both");
    const [rows, setRows] = useState<RecentMappingRow[]>([]);
    const [offset, setOffset] = useState<number>(0);
    const [hasMore, setHasMore] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    function fetchPage(modeArg: "both" | "zoho" | "cr", offsetArg: number, append: boolean) {
        setLoading(true);
        globalContext.GetToken().then((token: any) => {
            const headers = new Headers();
            headers.append("Authorization", "Bearer " + token);
            fetch(`${apiConfig.apiEndpoint}/MapAudit/RecentMappings?type=${modeArg}&limit=${PAGE_SIZE}&offset=${offsetArg}`, {
                method: "GET",
                headers
            })
                .then(r => r.json())
                .then(
                    (data: RecentMappingsResult) => {
                        setRows(append ? [...rows, ...(data.rows ?? [])] : (data.rows ?? []));
                        setHasMore(!!data.hasMore);
                        setOffset(offsetArg + (data.rows?.length ?? 0));
                        setLoading(false);
                    },
                    () => setLoading(false)
                );
        });
    }

    // Initial load + reload on mode change.
    useEffect(() => {
        setRows([]);
        setOffset(0);
        setHasMore(false);
        fetchPage(mode, 0, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const fmt = (iso?: string) => {
        if (!iso) return "—";
        const d = new Date(iso);
        if (isNaN(d.getTime())) return iso;
        return d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
    };

    const ToggleBtn = ({ value, label }: { value: "both" | "zoho" | "cr"; label: string }) => (
        <button
            onClick={() => setMode(value)}
            className={`px-4 py-1.5 text-sm font-semibold border ${
                mode === value
                    ? "bg-blue text-white border-blue"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
        >
            {label}
        </button>
    );

    const kindChip = (k: string) => {
        if (k === "ZohoToCore") return <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 font-semibold">Zoho → Core</span>;
        if (k === "CoreToCR") return <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-cyan-100 text-cyan-800 font-semibold">Core → CR</span>;
        return <span>{k}</span>;
    };

    return (
        <div>
            {/* 3-way segmented toggle */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="inline-flex rounded overflow-hidden">
                    <ToggleBtn value="both" label="Both" />
                    <ToggleBtn value="zoho" label="Zoho → Core" />
                    <ToggleBtn value="cr" label="Core → CR" />
                </div>
                <div className="text-xs text-gray-500 italic">
                    Showing most recent {rows.length} mapping{rows.length === 1 ? "" : "s"}.
                </div>
            </div>

            {loading && rows.length === 0 ? (
                <Loading />
            ) : rows.length === 0 ? (
                <div className="bg-gray-100 border border-gray-300 rounded p-4 text-gray-600 italic">
                    No mappings found.
                </div>
            ) : (
                <div className="overflow-x-auto bg-white border border-gray-300 rounded-lg shadow-sm">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="text-left px-3 py-2 border-b">When</th>
                                <th className="text-left px-3 py-2 border-b">Mapping</th>
                                <th className="text-left px-3 py-2 border-b">Learner</th>
                                <th className="text-left px-3 py-2 border-b">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={`${r.clientId}-${r.mappingKind}-${i}`} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 border-b whitespace-nowrap">{fmt(r.mappedAt)}</td>
                                    <td className="px-3 py-2 border-b">{kindChip(r.mappingKind)}</td>
                                    <td className="px-3 py-2 border-b">{[r.firstName, r.lastName].filter(Boolean).join(" ") || "—"}</td>
                                    <td className="px-3 py-2 border-b">
                                        <button
                                            onClick={() => onView(r.clientId)}
                                            className="bg-blue text-white text-xs font-semibold px-3 py-1 rounded hover:opacity-90"
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {hasMore && (
                <div className="mt-3 flex justify-center">
                    <button
                        onClick={() => fetchPage(mode, offset, true)}
                        disabled={loading}
                        className="border border-gray-300 px-4 py-2 rounded text-sm hover:bg-gray-50 disabled:opacity-50"
                    >
                        {loading ? "Loading…" : "Load more"}
                    </button>
                </div>
            )}
        </div>
    );
};

interface DuplicateClient {
    clientId: number;
    firstName?: string | null;
    lastName?: string | null;
    dob?: string | null;
    createdDate?: string | null;
    centerName?: string | null;
    active?: boolean | null;
    // Backend long; may lose precision as a JS number, so only tested for present/absent.
    zohoId?: number | null;
    centralReachContactId?: number | null;
    crActive?: boolean | null;
}
interface DuplicateLearnerGroup {
    learnerId: number;         // long; link/display via zohoId (string), never this.
    zohoId: string;
    learnerFirstName?: string | null;
    learnerLastName?: string | null;
    learnerDob?: string | null;
    learnerCenter?: string | null;
    learnerCreatedDate?: string | null;
    clients: DuplicateClient[];
}
interface DobNearMiss {
    learnerId: number;
    zohoId: string;
    firstName?: string | null;
    lastName?: string | null;
    center?: string | null;
    learnerDob: string;        // what Zoho has
    learnerCreatedDate?: string | null;
    clientId: number;
    clientDob: string;         // what Core has
    clientCreatedDate?: string | null;
    dobDaysApart: number;
    clientCenterName?: string | null;
    clientActive?: boolean | null;
    clientZohoId?: number | null;
    clientCentralReachContactId?: number | null;
    clientCrActive?: boolean | null;
}

const boolLabel = (b?: boolean | null) => (b === true ? "Yes" : b === false ? "No" : "—");

const mappedBadge = (mapped: boolean) =>
    mapped
        ? <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 font-semibold">Mapped</span>
        : <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-700 font-semibold">Unmapped</span>;

// Two sections behind one tab: true duplicates (exact DOB + fuzzy name, center ignored) and
// possible mistyped-DOB near-misses (name + center identical, DOB differs). Both scans are
// read-only and run against the full learner/client tables, so they fetch once on mount.
const DuplicatesTab = ({
    globalContext,
    onOpenClient,
    onOpenLearner,
}: {
    globalContext: any;
    onOpenClient: (clientId: number) => void;
    onOpenLearner: (zohoId: string) => void;
}) => {
    const [section, setSection] = useState<"dupes" | "nearmiss">("dupes");
    const [dupes, setDupes] = useState<DuplicateLearnerGroup[]>([]);
    const [nearMisses, setNearMisses] = useState<DobNearMiss[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [loaded, setLoaded] = useState<boolean>(false);

    function load() {
        setLoading(true);
        globalContext.GetToken().then((token: any) => {
            const headers = new Headers();
            headers.append("Authorization", "Bearer " + token);
            const opts = { method: "GET", headers };
            Promise.all([
                fetch(`${apiConfig.apiEndpoint}/MapAudit/GetDuplicateLearners`, opts).then(r => r.json()),
                fetch(`${apiConfig.apiEndpoint}/MapAudit/GetDobNearMisses`, opts).then(r => r.json()),
            ]).then(
                ([d, n]: [DuplicateLearnerGroup[], DobNearMiss[]]) => {
                    setDupes(Array.isArray(d) ? d : []);
                    setNearMisses(Array.isArray(n) ? n : []);
                    setLoading(false);
                    setLoaded(true);
                },
                () => { setLoading(false); setLoaded(true); }
            );
        });
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const SectionBtn = ({ value, label }: { value: "dupes" | "nearmiss"; label: string }) => (
        <button
            onClick={() => setSection(value)}
            className={`px-4 py-1.5 text-sm font-semibold border ${
                section === value
                    ? "bg-blue text-white border-blue"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
        >
            {label}
        </button>
    );

    // Small gap → likely typo (red), medium → amber, large → probably a different child (gray).
    const gapBadge = (days: number) => {
        const cls =
            days <= 7 ? "bg-red-100 text-red-800" :
            days <= 31 ? "bg-amber-100 text-amber-800" :
                         "bg-gray-100 text-gray-700";
        return <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-semibold ${cls}`}>{days} day{days === 1 ? "" : "s"}</span>;
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="inline-flex rounded overflow-hidden">
                    <SectionBtn value="dupes" label={`Duplicate clients (${dupes.length})`} />
                    <SectionBtn value="nearmiss" label={`Possible mistyped DOB (${nearMisses.length})`} />
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="border border-gray-300 px-3 py-1.5 rounded text-sm hover:bg-gray-50 disabled:opacity-50"
                >
                    {loading ? "Scanning…" : "Refresh"}
                </button>
            </div>

            {loading && !loaded ? (
                <Loading />
            ) : section === "dupes" ? (
                dupes.length === 0 ? (
                    <div className="bg-gray-100 border border-gray-300 rounded p-4 text-gray-600 italic">
                        No duplicate client records found.
                    </div>
                ) : (
                    // One table for every group (with a colspan header band per learner) so the
                    // columns line up across groups instead of each group sizing independently.
                    <div className="overflow-auto bg-white border border-gray-300 rounded-lg shadow-sm" style={{ maxHeight: "70vh" }}>
                        <table className="w-full text-sm">
                            <thead>
                                <tr>
                                    <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">ClientId</th>
                                    <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">First</th>
                                    <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">Last</th>
                                    <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">DOB</th>
                                    <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">Created</th>
                                    <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">Center</th>
                                    <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">Core Active</th>
                                    <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">Zoho mapped?</th>
                                    <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">CR ContactId</th>
                                    <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">CR Active</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dupes.map((g, gi) => (
                                    <React.Fragment key={g.zohoId}>
                                        {gi > 0 && (
                                            <tr aria-hidden="true">
                                                <td colSpan={10} className="p-0"><div className="h-3" /></td>
                                            </tr>
                                        )}
                                        <tr className="bg-gray-50 border-t border-b border-gray-300">
                                            <td colSpan={10} className="px-3 py-2">
                                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                                    <div>
                                                        <button
                                                            onClick={() => onOpenLearner(g.zohoId)}
                                                            className="text-blue font-semibold underline text-left"
                                                            title="Open this Zoho learner in the Search tab"
                                                        >
                                                            {[g.learnerFirstName, g.learnerLastName].filter(Boolean).join(" ") || "(no name)"}
                                                        </button>
                                                        <span className="text-gray-500 text-sm ml-2">DOB {fmtDate(g.learnerDob)}</span>
                                                        <span className="text-gray-500 text-sm ml-2">· {g.learnerCenter || "—"}</span>
                                                        <span className="text-gray-500 text-sm ml-2">· Created {fmtDate(g.learnerCreatedDate)}</span>
                                                        <span className="text-gray-400 text-xs ml-2">Zoho {g.zohoId}</span>
                                                    </div>
                                                    <span className="inline-block px-3 py-1 text-xs rounded-full bg-red-100 text-red-800 font-semibold">
                                                        {g.clients.length} duplicate client rows
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                        {g.clients.map(c => (
                                            <tr key={c.clientId} className="hover:bg-gray-50">
                                                <td className="px-3 py-2 border-b">
                                                    <button
                                                        onClick={() => onOpenClient(c.clientId)}
                                                        className="text-blue underline font-semibold"
                                                        title="Open this Core client in the Search tab"
                                                    >
                                                        {c.clientId}
                                                    </button>
                                                </td>
                                                <td className="px-3 py-2 border-b">{c.firstName ?? "—"}</td>
                                                <td className="px-3 py-2 border-b">{c.lastName ?? "—"}</td>
                                                <td className="px-3 py-2 border-b whitespace-nowrap">{fmtDate(c.dob)}</td>
                                                <td className="px-3 py-2 border-b whitespace-nowrap">{fmtDate(c.createdDate)}</td>
                                                <td className="px-3 py-2 border-b">{c.centerName ?? "—"}</td>
                                                <td className="px-3 py-2 border-b">{boolLabel(c.active)}</td>
                                                <td className="px-3 py-2 border-b">{mappedBadge(c.zohoId != null)}</td>
                                                <td className="px-3 py-2 border-b">{c.centralReachContactId ?? "—"}</td>
                                                <td className="px-3 py-2 border-b">{boolLabel(c.crActive)}</td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            ) : (
                nearMisses.length === 0 ? (
                    <div className="bg-gray-100 border border-gray-300 rounded p-4 text-gray-600 italic">
                        No possible mistyped-DOB records found.
                    </div>
                ) : (
                    <>
                        <div className="text-xs text-gray-500 italic mb-2">
                            Same name + center as a Core client, but a different DOB — likely a mistyped date.
                            Smallest day-gaps (most probable typos) are listed first.
                        </div>
                        <div className="overflow-auto bg-white border border-gray-300 rounded-lg shadow-sm" style={{ maxHeight: "70vh" }}>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr>
                                        <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">Learner</th>
                                        <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">Center</th>
                                        <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">Zoho DOB</th>
                                        <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">Core DOB</th>
                                        <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">Days apart</th>
                                        <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">ClientId</th>
                                        <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">Created</th>
                                        <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">Core Active</th>
                                        <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">Zoho mapped?</th>
                                        <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">CR ContactId</th>
                                        <th className="text-left px-3 py-2 border-b bg-gray-100 sticky top-0 z-10">CR Active</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {nearMisses.map((n, i) => (
                                        <tr key={`${n.zohoId}-${n.clientId}-${i}`} className="hover:bg-gray-50">
                                            <td className="px-3 py-2 border-b">
                                                <button
                                                    onClick={() => onOpenLearner(n.zohoId)}
                                                    className="text-blue underline font-semibold text-left"
                                                    title="Open this Zoho learner in the Search tab"
                                                >
                                                    {[n.firstName, n.lastName].filter(Boolean).join(" ") || "(no name)"}
                                                </button>
                                                <div className="text-xs text-gray-400">Learner created {fmtDate(n.learnerCreatedDate)}</div>
                                            </td>
                                            <td className="px-3 py-2 border-b">{n.center ?? "—"}</td>
                                            <td className="px-3 py-2 border-b whitespace-nowrap">{fmtDate(n.learnerDob)}</td>
                                            <td className="px-3 py-2 border-b whitespace-nowrap">{fmtDate(n.clientDob)}</td>
                                            <td className="px-3 py-2 border-b whitespace-nowrap">{gapBadge(n.dobDaysApart)}</td>
                                            <td className="px-3 py-2 border-b">
                                                <button
                                                    onClick={() => onOpenClient(n.clientId)}
                                                    className="text-blue underline font-semibold"
                                                    title="Open this Core client in the Search tab"
                                                >
                                                    {n.clientId}
                                                </button>
                                            </td>
                                            <td className="px-3 py-2 border-b whitespace-nowrap">{fmtDate(n.clientCreatedDate)}</td>
                                            <td className="px-3 py-2 border-b">{boolLabel(n.clientActive)}</td>
                                            <td className="px-3 py-2 border-b">{mappedBadge(n.clientZohoId != null)}</td>
                                            <td className="px-3 py-2 border-b">{n.clientCentralReachContactId ?? "—"}</td>
                                            <td className="px-3 py-2 border-b">{boolLabel(n.clientCrActive)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )
            )}
        </div>
    );
};

const DrawerContent = ({ type, data }: { type: "zoho" | "core" | "cr"; data: any }) => {
    if (data?.error) return <div className="text-red-700">{data.error}</div>;

    if (type === "zoho") {
        return <KeyValueTable obj={data.learner} title="Zoho Fields" emptyMsg="Zoho record not found." />;
    }

    if (type === "core") {
        return (
            <>
                <KeyValueTable obj={data.client} title="TblClient Fields" emptyMsg="Client not found." />
                <h4 className="font-semibold mt-5 mb-2">Audit History</h4>
                {(!data.history || data.history.length === 0) ? (
                    <div className="text-gray-500 italic">No audit entries.</div>
                ) : (
                    <table className="w-full text-sm border">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="text-left p-2 border-b">When</th>
                                <th className="text-left p-2 border-b">Source</th>
                                <th className="text-left p-2 border-b">Event</th>
                                <th className="text-left p-2 border-b">By</th>
                                <th className="text-left p-2 border-b">Detail</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.history.map((h: any, i: number) => (
                                <tr key={i} className="hover:bg-gray-50">
                                    <td className="p-2 border-b">{fmtDateTime(h.when)}</td>
                                    <td className="p-2 border-b">{h.source}</td>
                                    <td className="p-2 border-b">{h.event}</td>
                                    <td className="p-2 border-b">{h.updatedBy}</td>
                                    <td className="p-2 border-b">{h.detail}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </>
        );
    }

    if (type === "cr") {
        return (
            <>
                <KeyValueTable obj={data.contact} title="CR Contact Fields" emptyMsg="CR contact not found." />
                <h4 className="font-semibold mt-5 mb-2">Audit History</h4>
                {(!data.changesets || data.changesets.length === 0) ? (
                    <div className="text-gray-500 italic">No audit changesets.</div>
                ) : (
                    <div className="space-y-3">
                        {data.changesets.map((cs: any, i: number) => (
                            <div key={i} className="border rounded p-2 bg-gray-50">
                                <div className="text-sm font-semibold">
                                    {fmtDateTime(cs.changeDatetime)} — {cs.updatedBy}
                                    <span className="ml-2 text-xs text-gray-500">{cs.changesetId}</span>
                                </div>
                                <table className="w-full text-xs mt-2">
                                    <thead><tr className="text-gray-600">
                                        <th className="text-left">Field</th>
                                        <th className="text-left">Was</th>
                                        <th className="text-left">Became</th>
                                    </tr></thead>
                                    <tbody>
                                        {cs.fields.map((f: any, j: number) => (
                                            <tr key={j}>
                                                <td className="pr-3">{f.fieldName}</td>
                                                <td className="pr-3 text-red-700">{f.currentValue ?? "—"}</td>
                                                <td className="text-green-700">{f.updatedValue ?? "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))}
                    </div>
                )}
            </>
        );
    }

    return null;
};

const KeyValueTable = ({ obj, title, emptyMsg }: { obj: any; title: string; emptyMsg: string }) => {
    const [filter, setFilter] = useState<string>("");
    if (!obj) return <div className="text-gray-500 italic">{emptyMsg}</div>;
    const entries = Object.entries(obj).filter(([k, v]) => {
        // hide collection / nav properties (objects/arrays) — keep primitives & dates
        if (v && typeof v === "object" && !Array.isArray(v)) return false;
        if (Array.isArray(v)) return false;
        if (!filter) return true;
        return k.toLowerCase().includes(filter.toLowerCase()) ||
            String(v ?? "").toLowerCase().includes(filter.toLowerCase());
    });
    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold">{title}</h4>
                <input
                    type="text"
                    placeholder="Filter fields…"
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                />
            </div>
            <table className="w-full text-xs border">
                <tbody>
                    {entries.map(([k, v], i) => (
                        <tr key={i} className={i % 2 === 0 ? "bg-gray-50" : ""}>
                            <td className="p-1 border-b font-mono text-gray-700 w-1/3">{k}</td>
                            <td className="p-1 border-b break-all">{v === null || v === undefined ? <span className="text-gray-400 italic">null</span> : String(v)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export function ZohoCrMapAudit() {
    const authRequest = { ...loginRequest };
    return (
        <>
            <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={authRequest} errorComponent={ErrorComponent} loadingComponent={Loading}>
                <ProtectedPage RequiredRoles={['Administration.Learner.Write']}>
                    <ZohoCrMapAuditContent />
                </ProtectedPage>
            </MsalAuthenticationTemplate>
            <UnauthenticatedTemplate />
        </>
    );
}
